import React, { useState } from 'react';
import { Classes } from '@blueprintjs/core';
import {
  Form,
  FormInstance,
  SelectInput,
  TextArea,
  TextInput,
  FormErrors
} from '@lightbeamhealth/blueprint-form';
import { faEraser, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';

import { Row, Col } from '../../../../grid';
import Icon from '../../../atoms/icon';
import Button from '../../../molecules/button';
import { CellFunction } from '../cells';
import Callout from '../../callout';
import Tooltip from '../../tooltip';
import Modal from '../../modal';
import Toast from '../../toast';
import Card from '../../card';
import { AppKey } from '../../../../libs/apps';
import { useGetInvalidationConfig } from '../../../../libs/invalidation';
import { FormatTitle } from '../../../../libs/format';
import { ISession } from '../../../../libs/session';
import { PostJSON, BaseResponse } from '../../../../libs/request';

import { useSessionTable } from './context';

interface InvalidationFormProps {
  appKey: AppKey;
  sessionModel: string;
  session: ISession;
  onSuccess?: (...any) => any;
  onCancel?: (...any) => any;
}

interface InvalidationFormData {
  field: string;
  note?: string;
  newValue?: string;
}

interface PendingInvalidation {
  field: string;
  fieldDisplay: string;
  note?: string;
  newValue?: string | number;
}

export const InvalidationForm: React.FC<InvalidationFormProps> = ({
  appKey,
  sessionModel,
  session,
  onSuccess,
  onCancel
}) => {
  const { config } = useGetInvalidationConfig(appKey);
  const [pendingInvalidations, setPendingInvalidations] = useState<
    PendingInvalidation[]
  >([]);
  const form = new FormInstance<InvalidationFormData>({
    field: '',
    note: '',
    newValue: ''
  });
  // Get already selected field keys to exclude from options
  const selectedFields = new Set(pendingInvalidations.map((p) => p.field));

  const fieldOptions = [
    {
      label: '-- Select Field --',
      value: ''
    }
  ];
  const allAvailableFields: string[] = [];
  if (config && config[sessionModel]) {
    for (const key of config[sessionModel].keys) {
      // Only offer EPXHeart BP invalidation when the patient's schedule
      // tracks BP; allow invalidating null values for manual entry to
      // stay consistent with HTN BP and CHF weight invalidation.
      if (appKey === 'epxheart' && (key === 'bpUpper' || key === 'bpLower')) {
        const anySession = session as any;
        const tracksBP =
          anySession?.schedule?.askBloodPressure === true ||
          anySession?.isBP === true ||
          anySession?.bpUpper != null ||
          anySession?.bpLower != null;
        if (!tracksBP) {
          continue;
        }
      }
      allAvailableFields.push(key);
      if (!selectedFields.has(key)) {
        const display = config[sessionModel].keyDisplays[key];
        fieldOptions.push({
          label: display || FormatTitle(key),
          value: key
        });
      }
    }
  }

  // Determine if this is a multi-field session
  const isMultiField = allAvailableFields.length > 1;
  const validate = (data: InvalidationFormData): FormErrors => {
    const errors: FormErrors = {};
    if (!isMultiField) {
      // Single field mode - field is required
      if (!data.field || data.field.trim().length === 0) {
        errors.field = 'You must select a field to invalidate!';
      }
    }
    // In multi-field mode, validation is more lenient for the form since we have Add button
    return errors;
  };

  // Helper functions for managing pending invalidations
  const addPendingInvalidation = (data: InvalidationFormData) => {
    if (!data.field) return;

    const fieldDisplay =
      config?.[sessionModel]?.keyDisplays?.[data.field] ||
      FormatTitle(data.field);
    const newPending: PendingInvalidation = {
      field: data.field,
      fieldDisplay,
      note: data.note,
      newValue: data.newValue
    };

    setPendingInvalidations((prev) => [...prev, newPending]);
    // Reset form for next selection
    form.getForm().resetForm();
  };

  const removePendingInvalidation = (fieldToRemove: string) => {
    setPendingInvalidations((prev) =>
      prev.filter((p) => p.field !== fieldToRemove)
    );
  };

  // Check if current form can be added (requires a field selection)
  const canAddCurrentForm = (data: InvalidationFormData): boolean => {
    return !!(data.field && data.field.trim().length > 0);
  };

  // Request
  const [isLoading, setIsLoading] = useState(false);
  const onSubmit = (data: InvalidationFormData) => {
    if (isLoading) {
      return;
    }

    let invalidationsToSubmit: PendingInvalidation[] = [];

    if (isMultiField) {
      // For multi-field, submit all pending invalidations
      invalidationsToSubmit = [...pendingInvalidations];
    } else {
      // For single field, submit the current form data
      const fieldDisplay =
        config?.[sessionModel]?.keyDisplays?.[data.field] ||
        FormatTitle(data.field);
      invalidationsToSubmit = [
        {
          field: data.field,
          fieldDisplay,
          note: data.note,
          newValue: data.newValue
        }
      ];
    }

    if (invalidationsToSubmit.length === 0) {
      return;
    }

    const pluralText =
      invalidationsToSubmit.length > 1 ? 'invalidations' : 'invalidation';
    Modal.confirm({
      title: `About to process ${invalidationsToSubmit.length} ${pluralText}`,
      content:
        'Are you sure you want to proceed with the invalidation? This action cannot be undone.',
      onOk: () => {
        setIsLoading(true);

        // Process invalidations sequentially to avoid race conditions
        const processInvalidationsSequentially = async () => {
          const results: Array<{
            response: BaseResponse;
            invalidation: PendingInvalidation;
          }> = [];

          for (let invalidation of invalidationsToSubmit) {
            try {
              const response = await PostJSON<BaseResponse>({
                url: '/data/invalidate/',
                params: {
                  app: appKey,
                  sessionModel,
                  sessionId: session.id,
                  patientId: session.patientId,
                  key: invalidation.field,
                  note: invalidation.note,
                  newValue: invalidation.newValue
                }
              });
              results.push({ response, invalidation });
            } catch (error) {
              // If one fails, we still want to collect the error
              const safeMessage =
                error instanceof Error ? error.message : String(error);
              results.push({
                response: {
                  status: 'FAIL',
                  message: safeMessage
                },
                invalidation
              });
            }
          }
          return results;
        };

        processInvalidationsSequentially()
          .then((results) => {
            setIsLoading(false);

            // Check if all responses were successful
            const failedResults = results.filter(
              (result) => result.response.status !== 'OK'
            );

            if (failedResults.length > 0) {
              const failedFields = failedResults.map((result, index) => (
                <div
                  key={result.invalidation.field}
                  className={index > 0 ? 'mt-2' : ''}
                >
                  <strong>{result.invalidation.fieldDisplay}:</strong>{' '}
                  {result.response.message}
                </div>
              ));

              Modal.error({
                title: `Error Processing ${failedResults.length} of ${results.length} Invalidations`,
                content: (
                  <div>
                    <p className="mb-3">
                      The following fields failed to invalidate:
                    </p>
                    <div className="pa-3 bg-gray-50 border border-gray-200 rounded">
                      {failedFields}
                    </div>
                  </div>
                )
              });
              return;
            }

            const hasUpdates = invalidationsToSubmit.some((inv) => {
              if (inv.newValue === null || inv.newValue === undefined) {
                return false;
              }
              const valueString = String(inv.newValue);
              return valueString.trim().length > 0;
            });
            const successfulCount = results.length;
            const message = hasUpdates
              ? `Successfully processed ${successfulCount} ${pluralText}!`
              : `Successfully invalidated ${successfulCount} field${
                  successfulCount > 1 ? 's' : ''
                }!`;

            Toast({
              intent: 'success',
              message: message
            });
            if (onSuccess) {
              onSuccess();
            }
          })
          .catch((error) => {
            setIsLoading(false);
            const safeMessage =
              error instanceof Error ? error.message : String(error);
            Modal.error({
              title: 'Error Processing Invalidations',
              content: safeMessage
            });
          });
      }
    });
  };
  if (!session) {
    return null;
  }
  return (
    <>
      <div className={Classes.DIALOG_HEADER}>
        <h3>Invalidate Field Value</h3>
      </div>
      <div className={Classes.DIALOG_BODY}>
        <Callout intent="primary" className="mb-8">
          You should only invalidate an entry if you received confirmation that
          the entry is not valid. You can use the note to document the details.
          This action will be recorded.
        </Callout>
        <Callout intent="warning" className="mb-8">
          Note: Invalidating a value here does not change it in the EHR. You
          will still need to update the value in the EHR separately.
        </Callout>
        <Form form={form} validate={validate} onSubmit={onSubmit}>
          {(props) => {
            const field = props.values.field;
            let displayValue = '';
            if (field == null || field === '') {
              displayValue = '--';
            } else if (session.answers && session.answers[field] != null) {
              displayValue = session.answers[field];
            } else if (session[field] != null) {
              displayValue = session[field];
            }
            return (
              <>
                <Row>
                  <Col sm={12}>
                    <SelectInput
                      label="Field to Invalidate"
                      name="field"
                      options={fieldOptions}
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <b className="bp5-pseudo-label">Current Value</b>
                    <p className="bp5-pseudo-input-text">{displayValue}</p>
                  </Col>
                  <Col sm={12}>
                    <TextInput
                      label="New Value (Optional)"
                      name="newValue"
                      fill
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24}>
                    <TextArea
                      label="Notes (Optional)"
                      name="note"
                      fill
                      style={{
                        height: '120px'
                      }}
                    />
                  </Col>
                </Row>
                {isMultiField && (
                  <Row>
                    <Col xs={24} className="text-right mt-4">
                      <Button
                        icon={<Icon icon={faPlus} />}
                        intent="primary"
                        onClick={() => addPendingInvalidation(props.values)}
                        disabled={!canAddCurrentForm(props.values)}
                      >
                        Add Field
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
            );
          }}
        </Form>
        {isMultiField && pendingInvalidations.length > 0 && (
          <div className="mt-6">
            <h4 className="mb-3">
              Pending Invalidations ({pendingInvalidations.length})
            </h4>
            <div>
              {pendingInvalidations.map((pending, index) => {
                const displayValue =
                  session.answers?.[pending.field] ??
                  session[pending.field] ??
                  '--';
                const isLastCard = index === pendingInvalidations.length - 1;
                return (
                  <Card
                    key={pending.field}
                    className={`p-3 ${!isLastCard ? 'mb-3' : ''}`}
                  >
                    <Row>
                      <Col xs={20}>
                        <div className="flex flex-col">
                          <div className="font-medium text-sm">
                            <strong>{pending.fieldDisplay}</strong>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Current:{' '}
                            <span className="font-mono">{displayValue}</span>
                          </div>
                          {pending.newValue !== undefined &&
                            pending.newValue !== null && (
                              <div className="text-xs text-blue-600 mt-1">
                                New Value:{' '}
                                <span className="font-mono">
                                  {String(pending.newValue).trim().length > 0
                                    ? pending.newValue
                                    : '(blank)'}
                                </span>
                              </div>
                            )}
                          {pending.note && (
                            <div className="text-xs text-gray-600 mt-1">
                              Note:{' '}
                              <span className="italic">{pending.note}</span>
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col xs={4} className="text-right">
                        <Tooltip content="Remove">
                          <Button
                            icon={
                              <Icon icon={faTrash} className="text-red-500" />
                            }
                            onClick={() =>
                              removePendingInvalidation(pending.field)
                            }
                            bare
                            variant="minimal"
                            size="small"
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            intent="success"
            onClick={() => form.getForm().submitForm()}
            disabled={
              isLoading || (isMultiField && pendingInvalidations.length === 0)
            }
          >
            {isMultiField
              ? `Process ${pendingInvalidations.length} Invalidation${
                  pendingInvalidations.length !== 1 ? 's' : ''
                }`
              : 'Confirm Invalidation'}
          </Button>
        </div>
      </div>
    </>
  );
};

export const InvalidationCell = <T extends ISession>(
  appKey: AppKey,
  sessionModel: string
): CellFunction<T> => {
  return ({ row: { original: session } }) => {
    const { openInvalidation } = useSessionTable();
    return (
      <>
        <Tooltip content="Invalidate Field Value">
          <Button
            icon={<Icon icon={faEraser} className="text-brand" />}
            onClick={() => openInvalidation(appKey, sessionModel, session)}
            bare
            variant="minimal"
          />
        </Tooltip>
      </>
    );
  };
};
